import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export interface SeoConfig {
    title: string;
    description: string;
    canonicalPath?: string; // e.g. '/service/full-flex'
    ogImage?: string;
    ogType?: string;
    noindex?: boolean;
}

const BASE_URL = 'https://www.usinaco.work';
const DEFAULT_IMAGE = `${BASE_URL}/assets/img/og-image.jpg`;

@Injectable({ providedIn: 'root' })
export class SeoService {

    private meta       = inject(Meta);
    private title      = inject(Title);
    private router     = inject(Router);
    private document   = inject(DOCUMENT);
    private platformId = inject(PLATFORM_ID);

    /**
     * Actualiza todos los meta tags de la página:
     * title, description, robots, canonical, Open Graph y Twitter Card.
     */
    update(config: SeoConfig): void {
        const fullUrl     = `${BASE_URL}${config.canonicalPath ?? this.router.url}`;
        const ogImage     = config.ogImage ?? DEFAULT_IMAGE;
        const ogType      = config.ogType ?? 'website';
        const robotsValue = config.noindex ? 'noindex, nofollow' : 'index, follow';

        // ----- Title -----
        this.title.setTitle(config.title);

        // ----- Meta básicos -----
        this.setTag('name', 'description', config.description);
        this.setTag('name', 'robots', robotsValue);

        // ----- Canonical -----
        this.setCanonical(fullUrl);

        // ----- Open Graph -----
        this.setTag('property', 'og:type',        ogType);
        this.setTag('property', 'og:url',         fullUrl);
        this.setTag('property', 'og:title',       config.title);
        this.setTag('property', 'og:description', config.description);
        this.setTag('property', 'og:image',       ogImage);
        this.setTag('property', 'og:locale',      'es_AR');
        this.setTag('property', 'og:site_name',   'Usina Cowork');

        // ----- Twitter Card -----
        this.setTag('name', 'twitter:card',        'summary_large_image');
        this.setTag('name', 'twitter:title',       config.title);
        this.setTag('name', 'twitter:description', config.description);
        this.setTag('name', 'twitter:image',       ogImage);
    }

    /**
     * Inyecta un bloque de JSON-LD Schema.org en el <head>.
     * Si ya existe uno previo con el mismo id, lo reemplaza.
     * NOTA: Solo en browser (no SSR) para evitar timeouts en pre-rendering.
     */
    injectSchema(schema: object, id: string = 'schema-default'): void {
        if (!isPlatformBrowser(this.platformId)) return;
        const existingEl = this.document.getElementById(id);
        if (existingEl) existingEl.remove();

        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.id   = id;
        script.text = JSON.stringify(schema);
        this.document.head.appendChild(script);
    }

    /** Limpia los schemas inyectados (útil al destruir el componente) */
    removeSchema(id: string = 'schema-default'): void {
        if (!isPlatformBrowser(this.platformId)) return;
        const el = this.document.getElementById(id);
        if (el) el.remove();
    }

    // ─── Helpers privados ──────────────────────────────────────────────────────

    private setTag(attrName: 'name' | 'property', attrValue: string, content: string): void {
        const selector = `${attrName}="${attrValue}"`;
        if (this.meta.getTag(selector)) {
            this.meta.updateTag({ [attrName]: attrValue, content });
        } else {
            this.meta.addTag({ [attrName]: attrValue, content });
        }
    }

    private setCanonical(url: string): void {
        const existing = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (existing) {
            existing.href = url;
        } else {
            const link  = this.document.createElement('link');
            link.rel    = 'canonical';
            link.href   = url;
            this.document.head.appendChild(link);
        }
    }
}
